/**
 * `studio-chooser.ts` — staff-person-login-studio-switcher (2026-09-27). The
 * decision the layout takes the moment `/api/whoami` lands: render the page,
 * render the studio chooser, or move the tab to its canonical `/s/<slug>/`
 * URL first.
 *
 * # Why a pure function
 *
 * Three facts meet here and each has a failure mode that would be invisible
 * in a component: the server's answer (`studio` null or not), the tab's RAW
 * URL (prefix or not — the router's `useLocation()` has already had the
 * basename stripped, so it cannot tell), and the deep link that must survive
 * whichever way the tab goes. Getting the fold wrong shows up as a person
 * landing on `/` instead of the member they clicked, or as a single-studio
 * person's tab living un-prefixed forever (which works, until they are added
 * to a second studio and every request in that tab turns into a 403). So the
 * fold is one function over plain values, and the layout only acts on its
 * answer.
 *
 * # The three answers
 *
 *   - `canonicalize` — the server resolved a studio but the URL carries no
 *     prefix (a single-studio person at `/`, or an un-prefixed deep link the
 *     server could auto-resolve). The layout does `window.location.replace(to)`
 *     — a FULL load, so `main.tsx` re-derives the basename and every later
 *     link and header agrees with the URL. The inner path, search and hash
 *     are carried, so `/members/1?tab=2` becomes `/s/<slug>/members/1?tab=2`.
 *   - `choose` — `studio` is null: the person belongs to several studios and
 *     the request selected none, or named one they are not in. The chooser
 *     renders instead of the page; `carried` is the inner path the choice
 *     must land on (`/` when there is nothing to carry), so a deep link
 *     opened without a prefix still reaches the member after the pick.
 *   - `in_studio` — the prefix is present and the server resolved it. Note
 *     "prefix present but `studio.slug` differs from the prefix" cannot occur:
 *     the server never auto-resolves a request that NAMED a slug — a header
 *     naming a studio the person is not in is `unselected`, never their only
 *     studio (`selectStudio` in `apps/dashboard/src/auth.ts`) — so a resolved
 *     `studio` under a prefix is always that prefix's own studio.
 *
 * The location this takes is the RAW `window.location` triple, NOT the
 * router's — see above.
 */

import type { WhoamiResponse } from "@layer/dashboard/types";
import { activeStudioSlug, stripStudioPrefix, studioPath } from "./studio-scope";

/** The raw `window.location` triple (the router's has the basename stripped). */
export interface ChooserLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

export type ChooserDecision =
  | { readonly kind: "canonicalize"; readonly to: string }
  | { readonly kind: "choose"; readonly carried: string }
  | { readonly kind: "in_studio" };

/**
 * The basename-relative path + search + hash this tab is on — what a studio
 * choice or a canonicalizing redirect must land on. `/` when there is nothing
 * to carry, so callers can pass it straight to `studioPath`.
 */
export function carriedInnerPath(location: ChooserLocation): string {
  return `${stripStudioPrefix(location.pathname)}${location.search}${location.hash}`;
}

export function chooserDecision(
  whoami: Pick<WhoamiResponse, "studio">,
  location: ChooserLocation,
): ChooserDecision {
  if (whoami.studio === null) {
    return { kind: "choose", carried: carriedInnerPath(location) };
  }
  if (activeStudioSlug(location.pathname) === null) {
    return {
      kind: "canonicalize",
      to: studioPath(whoami.studio.slug, carriedInnerPath(location)),
    };
  }
  return { kind: "in_studio" };
}
